import React, { useState, useEffect } from 'react'
import {
  Table, Button, Space, Card, Form, Input, AutoComplete,
  InputNumber, Select, message, DatePicker, Divider, Tag, Empty, Tooltip, Popconfirm, Tabs, Col,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ThunderboltOutlined,
  UserOutlined, SettingOutlined, EyeOutlined, EnvironmentOutlined,
  CheckCircleOutlined, ApartmentOutlined, BarChartOutlined, SearchOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useMasterData, type Generator, type GeneratorPlant } from '@/context/MasterDataContext'
import { useLeadCompanies } from '@/pages/Lead/useLeadCompanies'
import { leadStore } from '@/pages/Lead/leadStore'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import { useModulePermission } from '@/hooks/useModulePermission'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { genId } from '@/utils/genId'
import { generateCode } from '@/utils/codeGenerator'
import { formatIndianCompact, formatIndianFull, annualToMonthlyUnits, formatUnitsKWh } from '@/utils/numberFormat'
import { mappingStore } from '@/pages/Mapping/mappingStore'
import { ApiError } from '@/services/api'
import './MasterForm.css'

const BASE = '/master/generators'

const plantTypeColor = (t: string): string => {
  const lc = (t || '').toLowerCase()
  if (lc.includes('solar'))  return '#F59E0B'
  if (lc.includes('wind'))   return '#2563EB'
  if (lc.includes('hydro'))  return '#06B6D4'
  if (lc.includes('hybrid')) return '#059669'
  if (lc.includes('biomass') || lc.includes('gas') || lc.includes('thermal')) return '#7C3AED'
  return '#4A5568'
}

const MONO: React.CSSProperties = { fontFamily: 'JetBrains Mono, monospace' }

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '9px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
    <div style={{ width: 160, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0B1426', flex: 1 }}>{value || <span style={{ color: '#ccc' }}>—</span>}</div>
  </div>
)

const nextPlantId = (): string => genId('PL')

const GeneratorMaster: React.FC = () => {
  const { generators, powerTypes, supplyModes, injectingVoltages, edcs, deleteGenerator, saveGenerator, savePowerType, saveSupplyMode, masterDataLoaded } = useMasterData()
  const { mode, editId, isForm, goAdd, goEdit, goView, goList } = useMasterFormRoute(BASE)
  const { canAdd, canEdit, canDelete } = useModulePermission('Generator')
  const voltageOptions = injectingVoltages.filter(v => v.status === 'active').map(v => ({ value: v.value, label: `${v.value} ${v.unit}` }))
  const edcOptions = edcs.filter(e => e && e.status !== 'inactive').map(e => ({ value: e.name, label: `${e.name}${e.place ? ` (${e.place})` : ''}` }))

  /* Generator form */
  const [form]                          = Form.useForm()
  const [submitting, setSubmitting]     = useState(false)

  /* Plants state (local while editing a generator) */
  const [plants,        setPlants]        = useState<GeneratorPlant[]>([])
  const [plantModalOpen, setPlantModalOpen] = useState(false)
  const [editingPlantIdx, setEditingPlantIdx] = useState<number | null>(null)
  const [plantForm]                       = Form.useForm()
  const plantCapacityWatch = Form.useWatch('capacity', plantForm)

  const navigate = useNavigate()
  // Record shown by the full-page Detail view (mode === 'view').
  const viewRecord: Generator | undefined = mode === 'view' ? generators.find(g => g.id === editId) : undefined

  /* List search / filter */
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('')

  // Stay live on the "Mapped Consumers" tab as mappings are added/edited elsewhere.
  const [, setMappingTick] = useState(0)
  useEffect(() => {
    const bump = () => setMappingTick(t => t + 1)
    mappingStore.refresh().then(bump)
    window.addEventListener(mappingStore.eventName, bump)
    return () => window.removeEventListener(mappingStore.eventName, bump)
  }, [])

  // Company names from Generator-type leads, suggested in the name field
  // below — useLeadCompanies itself now gates this on Rule §14 (approved
  // proposal + signed agreement), so a lead isn't selectable here the moment
  // it's created, only once that deal is actually closed.
  const leadCompanies = useLeadCompanies('Generator')

  const powerTypeOptions = powerTypes
    .filter(pt => pt.status === 'active')
    .map(pt => ({ label: pt.name, value: pt.name }))

  const supplyModeOptions = supplyModes
    .filter(sm => sm.status === 'active')
    .map(sm => ({ label: sm.name, value: sm.name }))

  /* ── Stat summaries ── */
  const totalCap    = generators.reduce((s, g) => s + (g.plants || []).reduce((ps, p) => ps + Number(p.capacity || 0), 0), 0)
  const activeCount = generators.filter(g => g.status === 'active').length

  const filteredGenerators = generators.filter(g => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      g.name.toLowerCase().includes(q) ||
      (g.code || '').toLowerCase().includes(q) ||
      (g.gstin || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || g.status === filterStatus
    return matchSearch && matchStatus
  })

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    // Direct-URL guard: a "View"-only role can't reach the Add/Edit form even
    // by typing the address — only the buttons were hidden before this.
    if (mode === 'new' && !canAdd) { goList(); return }
    if (mode === 'edit' && !canEdit) { goList(); return }
    if (mode === 'new') {
      setPlants([])
      setPlantModalOpen(false)
      setEditingPlantIdx(null)
      form.resetFields()
    } else if (mode === 'edit') {
      const rec = generators.find(g => g.id === editId)
      if (rec) {
        setPlants(rec.plants || [])
        setPlantModalOpen(false)
        setEditingPlantIdx(null)
        form.setFieldsValue({
          name: rec.name,
          contactPersonName: rec.contactPersonName,
          designation: rec.designation,
          contactMobile: rec.contactMobile,
          contactEmail: rec.contactEmail,
          companyAddress: rec.companyAddress,
          gstin: rec.gstin,
          panNumber: rec.panNumber,
          cinNumber: rec.cinNumber,
          state: rec.state,
          tdsRate: rec.tdsRate,
          paymentTerms: rec.paymentTerms,
          bankDetails: rec.bankDetails,
          status: rec.status,
        })
      } else {
        goList()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editId, generators, canAdd, canEdit])

  const handleDelete = async (id: string) => {
    try {
      await deleteGenerator(id)
      message.success('Generator removed')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  /* ── Pick a Generator-type lead → auto-fill company contact details & plant info ── */
  const handleLeadCompanySelect = (companyName: string) => {
    const lead = leadStore.getLeads().find(
      l => (l.companyName || '').trim().toLowerCase() === companyName.trim().toLowerCase()
    )
    if (!lead) return
    const address = [lead.address, lead.city, lead.pinCode].map(s => (s || '').trim()).filter(Boolean).join(', ')
    form.setFieldsValue({
      name: lead.companyName,
      contactPersonName: lead.customerName || undefined,
      contactMobile: lead.contactNumber || undefined,
      contactEmail: lead.email || undefined,
      companyAddress: address || undefined,
      gstin: lead.gstNumber || undefined,
    })

    message.success(`Details filled from lead "${lead.companyName}" (Demand: ${lead.sanctionedDemand || 0} ${lead.demandUnit || 'KW'}, Drawal Voltage: ${lead.drawlVoltage || '—'}). Use "Add Plant" to add plant details.`)
  }

  /* ── Open plant add/edit ── */
  const openAddPlant = () => {
    setEditingPlantIdx(null)
    plantForm.resetFields()

    // Pre-fill plant capacity, demand, voltage from matching Lead
    const compName = form.getFieldValue('name')
    const matchedLead = compName
      ? leadStore.getLeads().find(l => (l.companyName || '').trim().toLowerCase() === (compName || '').trim().toLowerCase())
      : undefined

    if (matchedLead) {
      plantForm.setFieldsValue({
        capacity: matchedLead.requiredUnitsPerAnnum ? Number(matchedLead.requiredUnitsPerAnnum) : undefined,
        installedCapacityKw: matchedLead.sanctionedDemand ? Number(matchedLead.sanctionedDemand) : undefined,
        substationVoltage: matchedLead.drawlVoltage || undefined,
        injectingVoltage: matchedLead.drawlVoltage || undefined,
        typeOfPower: matchedLead.typeOfPower ? [matchedLead.typeOfPower] : undefined,
        modeOfSupply: matchedLead.modeOfSupply ? [matchedLead.modeOfSupply] : undefined,
        htServiceNumber: matchedLead.htscNumber || undefined,
        plantAddress: [matchedLead.address, matchedLead.city, matchedLead.pinCode].filter(Boolean).join(', '),
        edc: matchedLead.edcName || undefined,
      })
    }
    setPlantModalOpen(true)
  }

  const openEditPlant = (idx: number) => {
    const p = plants[idx]
    setEditingPlantIdx(idx)
    plantForm.setFieldsValue({
      ...p,
      cod: p.cod ? dayjs(p.cod, 'DD/MM/YYYY') : null,
      synchronizationDate: p.synchronizationDate ? dayjs(p.synchronizationDate, 'DD/MM/YYYY') : null,
    })
    setPlantModalOpen(true)
  }

  const handleRemovePlant = (idx: number) => {
    setPlants(prev => prev.filter((_, i) => i !== idx))
  }

  /* ── Save a plant ── */
  const handleSavePlant = () => {
    plantForm.validateFields().then(values => {
      const plant: GeneratorPlant = {
        ...values,
        cod: values.cod ? values.cod.format('DD/MM/YYYY') : '',
        synchronizationDate: values.synchronizationDate ? values.synchronizationDate.format('DD/MM/YYYY') : '',
        typeOfPower: values.typeOfPower || [],
        modeOfSupply: values.modeOfSupply || [],
        status: values.status || 'active',
        id: editingPlantIdx !== null ? plants[editingPlantIdx].id : nextPlantId(),
      }
      if (editingPlantIdx !== null) {
        setPlants(prev => prev.map((p, i) => i === editingPlantIdx ? plant : p))
      } else {
        setPlants(prev => [...prev, plant])
      }
      setPlantModalOpen(false)
      plantForm.resetFields()
    }).catch(() => {
      message.error('Please fill in all required plant fields.')
    })
  }

  /* ── Dynamic addition to masters ── */
  const handlePowerTypeChange = (values: string[]) => {
    values.forEach(v => {
      if (!powerTypes.some(pt => pt.name.toLowerCase() === v.toLowerCase())) {
        void (async () => {
          try {
            const code = await generateCode('power_types')
            await savePowerType({
              id: genId('PT'), code,
              name: v, description: 'Added from Generator form', status: 'active',
            }, false)
          } catch (err) {
            message.error(err instanceof ApiError ? err.message : "Couldn't add the new power type — check your connection and try again.")
          }
        })()
      }
    })
  }

  const handleSupplyModeChange = (values: string[]) => {
    values.forEach(v => {
      if (!supplyModes.some(sm => sm.name.toLowerCase() === v.toLowerCase())) {
        void (async () => {
          try {
            const code = await generateCode('supply_modes')
            await saveSupplyMode({
              id: genId('MS'), code,
              name: v, description: 'Added from Generator form', status: 'active',
            }, false)
          } catch (err) {
            message.error(err instanceof ApiError ? err.message : "Couldn't add the new supply mode — check your connection and try again.")
          }
        })()
      }
    })
  }

  /* ── Save generator ── */
  const onFinish = async (values: any) => {
    const isEdit = mode === 'edit'
    const nameTrim = (values.name || '').trim().toLowerCase()
    const gstinTrim = (values.gstin || '').trim().toLowerCase()
    const firstPlantHtsc = (plants[0]?.htServiceNumber || '').trim().toLowerCase()

    // ── Duplication Check: Company Name, GSTIN, or HT Service Number ──
    const duplicate = generators.find(g => {
      if (isEdit && g.id === editId) return false
      const sameName = (g.name || '').trim().toLowerCase() === nameTrim
      const sameGst = gstinTrim && (g.gstin || '').trim().toLowerCase() === gstinTrim
      const sameHtsc = firstPlantHtsc && g.plants?.some(p => (p.htServiceNumber || '').trim().toLowerCase() === firstPlantHtsc)
      return sameName || sameGst || sameHtsc
    })

    if (duplicate) {
      message.error(`Duplication not allowed: A generator with company name "${duplicate.name}" ${duplicate.gstin ? `or GST "${duplicate.gstin}"` : ''} already exists in Generator Master.`)
      return
    }

    let newCode: string
    if (isEdit) {
      newCode = editId!
    } else {
      try {
        newCode = await generateCode('generators')
      } catch (err) {
        message.error(err instanceof ApiError ? err.message : "Couldn't generate a code — check your connection and try again.")
        return
      }
    }

    const firstPlant = plants[0]
    const allTypes   = [...new Set(plants.flatMap(p => p.typeOfPower || []))]
    const allModes   = [...new Set(plants.flatMap(p => p.modeOfSupply || []))]
    const totalPlantCap = plants.reduce((s, p) => s + Number(p.capacity || 0), 0)
    const totalInstalledCapacityKw = plants.reduce((s, p) => s + Number(p.installedCapacityKw || 0), 0)

    const payload: Generator = {
      ...values,
      plants,
      typeOfPower:    allTypes,
      modeOfSupply:   allModes,
      plantType:      (firstPlant?.typeOfPower || []).join(' + ') || '',
      capacity:       totalPlantCap,
      installedCapacityKw: totalInstalledCapacityKw,
      htServiceNumber: firstPlant?.htServiceNumber || '',
      substationName: firstPlant?.substationName || '',
      state: values.state || 'Tamil Nadu',
      status: values.status || 'active',
      tdsRate: values.tdsRate !== undefined ? values.tdsRate : 2,
      paymentTerms: values.paymentTerms !== undefined ? values.paymentTerms : 30,
      bankDetails: values.bankDetails || '',
      ...(isEdit ? { id: editId! } : { id: newCode, code: newCode }),
    }

    setSubmitting(true)
    try {
      await saveGenerator(payload, isEdit)
      message.success(isEdit ? 'Generator updated' : 'Generator added')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  /* ── Table columns ── */
  const columns = [
    {
      title: 'Generator Name', dataIndex: 'name',
      render: (name: string, r: Generator) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1677ff' }}>
            {name}
          </div>
          <div style={{ fontSize: 11, color: '#8A93A6', ...MONO }}>{r.code} · {r.gstin || 'No GST'}</div>
        </div>
      ),
    },
    {
      title: 'Type of Power',
      render: (_: any, r: Generator) => {
        const types = [...new Set((r.plants || []).flatMap(p => p.typeOfPower || []))]
        return types.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {types.map(t => (
              <Tag key={t} style={{ borderRadius: 4, fontWeight: 600, fontSize: 11, color: '#fff', background: plantTypeColor(t), border: 'none' }}>{t}</Tag>
            ))}
          </div>
        ) : <span style={{ color: '#ccc' }}>—</span>
      },
    },
    {
      title: 'Proposed Annual Generation', align: 'right' as const,
      render: (_: any, r: Generator) => {
        const cap = (r.plants || []).reduce((s, p) => s + Number(p.capacity || 0), 0)
        return cap ? (
          <span style={{ ...MONO, fontWeight: 700 }}>
            {cap.toLocaleString('en-IN')} kWh/yr
            <div style={{ fontWeight: 400, fontSize: 11, color: '#8A93A6' }}>~{formatUnitsKWh(annualToMonthlyUnits(cap))}/mo</div>
          </span>
        ) : <span style={{ color: '#ccc' }}>—</span>
      },
    },
    {
      title: 'Installed Capacity', align: 'right' as const,
      render: (_: any, r: Generator) => {
        const kw = (r.plants || []).reduce((s, p) => s + Number(p.installedCapacityKw || 0), 0)
        return kw ? <span style={{ ...MONO, fontWeight: 700 }}>{kw.toLocaleString('en-IN')} kW</span>
                  : <span style={{ color: '#ccc' }}>—</span>
      },
    },
    {
      title: 'Mode of Supply',
      render: (_: any, r: Generator) => {
        const modes = [...new Set((r.plants || []).flatMap(p => p.modeOfSupply || []))]
        return modes.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {modes.map(m => (
              <Tag key={m} color="geekblue" style={{ borderRadius: 4, fontSize: 11 }}>{m}</Tag>
            ))}
          </div>
        ) : <span style={{ color: '#ccc' }}>—</span>
      },
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (s: string) => (
        <span className={`pill ${s === 'active' ? 'pill-green' : 'pill-neutral'}`}>
          {s === 'active' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: Generator) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="View">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => goView(record.id)} />
          </Tooltip>
          {canEdit && (
            <Tooltip title="Edit">
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
            </Tooltip>
          )}
          {canDelete && (
            <Popconfirm
              title="Delete this record?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(record.id)}
              okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete">
                <Button type="link" danger size="small" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  const col2: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }
  const col3: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }

  // ── Full-page Detail view ─────────────────────────────────────
  if (mode === 'view') {
    if (!viewRecord) {
      if (masterDataLoaded) goList()
      return null
    }
    return (
      <MasterFormShell
        title={viewRecord.name}
        subtitle={`${viewRecord.code} · ${viewRecord.gstin || 'No GST'}`}
        onBack={goList}
        extra={
          <Space>
            {canEdit && (
              <Tooltip title="Edit">
                <Button type="text" icon={<EditOutlined />} onClick={() => goEdit(viewRecord.id)} />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <Popconfirm
                  title="Delete this record?"
                  description="This action cannot be undone."
                  onConfirm={() => handleDelete(viewRecord.id)}
                  okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
                >
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Tooltip>
            )}
          </Space>
        }
      >
        <Tabs
          items={[
            {
              key: 'details',
              label: 'Details',
              children: (
                <div>
                  <span className={`pill ${viewRecord.status === 'active' ? 'pill-green' : 'pill-neutral'}`} style={{ marginBottom: 12, display: 'inline-block' }}>
                    {viewRecord.status === 'active' ? 'Active' : 'Inactive'}
                  </span>

                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#8A93A6', marginBottom: 8 }}>
                    Contact Details
                  </div>
                  <DetailRow label="Contact Person"    value={viewRecord.contactPersonName} />
                  <DetailRow label="Designation"       value={viewRecord.designation} />
                  <DetailRow label="Mobile"            value={viewRecord.contactMobile} />
                  <DetailRow label="Email"             value={viewRecord.contactEmail} />
                  <DetailRow label="Company Address"   value={viewRecord.companyAddress} />
                  <DetailRow label="GST Number"        value={<span style={MONO}>{viewRecord.gstin}</span>} />
                  <DetailRow label="PAN Number"        value={<span style={MONO}>{viewRecord.panNumber}</span>} />
                  <DetailRow label="Bank Details"      value={viewRecord.bankDetails} />

                  <Divider style={{ margin: '16px 0 8px' }} />
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#8A93A6', marginBottom: 12 }}>
                    Plants ({(viewRecord.plants || []).length})
                  </div>

                  {(viewRecord.plants || []).length === 0 && (
                    <Empty description="No plants added" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}

                  {(viewRecord.plants || []).map((p, i) => (
                    <div key={p.id} style={{
                      background: '#F7F8FB', borderRadius: 10, padding: 14, marginBottom: 10,
                      border: '1px solid #E4E8F0',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div>
                          <div style={{ fontWeight: 700, color: '#001A4D', fontSize: 13 }}>{p.plantName || `Plant ${i + 1}`}</div>
                          <div style={{ ...MONO, fontSize: 11, color: '#8A93A6' }}>{p.id}</div>
                        </div>
                        <div>
                          {(p.typeOfPower || []).map(t => (
                            <Tag key={t} style={{ borderRadius: 4, fontWeight: 600, fontSize: 10, color: '#fff', background: plantTypeColor(t), border: 'none' }}>{t}</Tag>
                          ))}
                        </div>
                      </div>
                      <DetailRow label="HT No."           value={<span style={MONO}>{p.htServiceNumber}</span>} />
                      <DetailRow label="Installed Capacity" value={p.installedCapacityKw ? <span style={MONO}>{Number(p.installedCapacityKw).toLocaleString('en-IN')} kW</span> : undefined} />
                      <DetailRow label="Plant Location"    value={p.plantAddress} />
                      <DetailRow label="Substation"        value={p.substationName} />
                      <DetailRow label="Mode of Supply"    value={(p.modeOfSupply || []).join(', ')} />
                      <DetailRow label="Proposed Annual Generation" value={<span style={MONO}>{Number(p.capacity || 0).toLocaleString('en-IN')} kWh/yr</span>} />
                      <DetailRow label="Proposed Monthly Generation" value={<span style={MONO}>{formatUnitsKWh(annualToMonthlyUnits(p.capacity))}/mo</span>} />
                      <DetailRow label="Commissioning"     value={p.cod} />
                      <DetailRow label="EDC"               value={p.edc} />
                      <DetailRow label="Meter No."         value={<span style={MONO}>{p.meterNumber}</span>} />
                      <DetailRow label="CT / PT Ratio"     value={<span style={MONO}>{p.ctRatio} / {p.ptRatio}</span>} />
                    </div>
                  ))}
                </div>
              ),
            },
            {
              key: 'consumers',
              label: 'Mapped Consumers',
              children: (() => {
                const genMappings = mappingStore.getAll().filter(m => m.generatorId === viewRecord.id)
                const rows = genMappings.flatMap(m => (m.consumers || []).map(c => ({ ...c, mappingId: m.id, plantName: m.plantName, htNumber: m.htNumber })))

                if (rows.length === 0) {
                  return <Empty description="No consumers mapped to this generator yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                }

                return (
                  <div>
                    {rows.map((r, i) => (
                      <div
                        key={`${r.mappingId}-${r.consumerId}-${i}`}
                        style={{
                          background: '#F7F8FB', borderRadius: 10, padding: 14, marginBottom: 10,
                          border: '1px solid #E4E8F0', cursor: 'pointer',
                        }}
                        onClick={() => navigate(`/mappings/${r.mappingId}`)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <div>
                            <div style={{ fontWeight: 700, color: '#1677ff', fontSize: 13 }}>{r.consumerName}</div>
                            <div style={{ fontSize: 11, color: '#8A93A6' }}>{r.plantName || '—'}{r.htNumber ? ` · ${r.htNumber}` : ''}</div>
                          </div>
                          <Tag color={r.status === 'active' ? 'green' : 'default'}>{r.status === 'active' ? 'Active' : 'Suspended'}</Tag>
                        </div>
                        <DetailRow label="Allocation %"     value={`${r.shareHolding}%`} />
                        <DetailRow label="Allocated Units"  value={r.allocatedUnits ? <span style={MONO}>{Number(r.allocatedUnits).toLocaleString('en-IN')} kWh/yr</span> : undefined} />
                        <DetailRow label="Sakthi Tariff"    value={r.tariff ? <span style={MONO}>₹{r.tariff}/kWh</span> : undefined} />
                        <DetailRow label="Effective From"   value={r.effectiveFrom} />
                        <DetailRow label="Effective To"     value={r.effectiveTo || 'Open-ended'} />
                      </div>
                    ))}
                  </div>
                )
              })(),
            },
          ]}
        />
      </MasterFormShell>
    )
  }

  // ── Full-page Add / Edit form ─────────────────────────────────
  if (isForm) {
    return (
      <MasterFormShell
        title={mode === 'edit' ? 'Edit Generator' : 'Add Generator'}
        subtitle="Generator company, contact details and its plants"
        onBack={goList}
        extra={mode === 'edit' && editId && canDelete ? (
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>

          {/* ══ SECTION 1: Communication Details ══ */}
          <Card
            title={
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
                <UserOutlined style={{ color: '#2563EB' }} /> Section 1 — Communication Details
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <div style={col2}>
              <Form.Item
                name="name"
                label="Generator Company Name"
                tooltip="Pick a company from Generator-type leads to auto-fill contact details"
                rules={[{ required: true, message: 'Required' }]}
              >
                <AutoComplete
                  options={leadCompanies.map(c => ({ value: c }))}
                  placeholder="Select from Generator leads"
                  allowClear
                  onSelect={handleLeadCompanySelect}
                  filterOption={(input, option) =>
                    (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
              <Form.Item name="contactPersonName" label="Contact Person Name">
                <Input placeholder="e.g. Ramesh Kumar" />
              </Form.Item>
            </div>
            <div style={col3}>
              <Form.Item name="designation" label="Designation">
                <Input placeholder="e.g. Director" />
              </Form.Item>
              <Form.Item name="contactMobile" label="Contact Mobile">
                <Input placeholder="9876543210" maxLength={10} />
              </Form.Item>
              <Form.Item name="contactEmail" label="Contact Email">
                <Input placeholder="name@company.com" type="email" />
              </Form.Item>
            </div>
            <Form.Item name="companyAddress" label="Company Address">
              <Input.TextArea rows={2} placeholder="Full company registered address" />
            </Form.Item>
            <div style={col3}>
              <Form.Item name="gstin" label="GST Number">
                <Input placeholder="33AABFW1234A1Z5" style={MONO} />
              </Form.Item>
              <Form.Item name="panNumber" label="PAN Number">
                <Input placeholder="AABFW1234A" style={{ ...MONO, textTransform: 'uppercase' }} maxLength={10} />
              </Form.Item>
              <Form.Item name="cinNumber" label="CIN Number">
                <Input placeholder="U40100TN2010PTC075432" style={MONO} />
              </Form.Item>
            </div>
            <div style={col3}>
              <Form.Item name="state" label="State" initialValue="Tamil Nadu">
                <Input placeholder="e.g. Tamil Nadu" />
              </Form.Item>
              <Form.Item name="tdsRate" label="TDS Rate (%)" initialValue={2}>
                <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="paymentTerms" label="Payment Terms (days)" initialValue={30}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <Form.Item name="bankDetails" label="Bank Details">
              <Input.TextArea rows={2} placeholder="Bank Name — Account No — IFSC Code" />
            </Form.Item>
            <Form.Item name="status" label="Status" initialValue="active">
              <Select options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
            </Form.Item>
          </Card>

          {/* ══ SECTION 2: Plants ══ */}
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
                  <EnvironmentOutlined style={{ color: '#059669' }} /> Section 2 — Plants
                  <span style={{ fontSize: 12, color: '#8A93A6', fontWeight: 400, marginLeft: 4 }}>
                    ({plants.length} added)
                  </span>
                </span>
                <Button type="dashed" icon={<PlusOutlined />} size="small" onClick={openAddPlant}>
                  Add Plant
                </Button>
              </div>
            }
            style={{ marginBottom: 16 }}
          >
            {plants.length === 0 && (
              <Empty description="No plants added yet. Click 'Add Plant' to begin." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
            {plants.map((p, idx) => (
              <div key={p.id} style={{
                background: '#F7F8FB', border: '1px solid #E4E8F0', borderRadius: 10,
                padding: '12px 16px', marginBottom: 10,
                display: 'flex', alignItems: 'flex-start', gap: 12,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, color: '#001A4D', fontSize: 13 }}>{p.plantName || p.id}</span>
                    <span style={{ ...MONO, fontSize: 11, color: '#8A93A6' }}>{p.id}</span>
                    {(p.typeOfPower || []).map(t => (
                      <Tag key={t} style={{ borderRadius: 4, fontWeight: 600, fontSize: 10, color: '#fff', background: plantTypeColor(t), border: 'none' }}>{t}</Tag>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: '#4A5568' }}>
                    HT: <span style={MONO}>{p.htServiceNumber || '—'}</span>
                    {p.installedCapacityKw && <span> · <strong>{Number(p.installedCapacityKw).toLocaleString('en-IN')} kW</strong> installed</span>}
                    {p.substationName && <span> · {p.substationName}</span>}
                    {p.capacity && <span> · <strong>{Number(p.capacity).toLocaleString('en-IN')} kWh/yr</strong> (~{formatUnitsKWh(annualToMonthlyUnits(p.capacity))}/mo)</span>}
                    {p.cod && <span> · COD: {p.cod}</span>}
                  </div>
                </div>
                <Space size={4}>
                  <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditPlant(idx)} />
                  <Button type="text" size="small" icon={<DeleteOutlined />} danger onClick={() => handleRemovePlant(idx)} />
                </Space>
              </div>
            ))}

            {/* ── Inline Add / Edit Plant editor (opens below, not a popup) ── */}
            {plantModalOpen && (
              <div
                onKeyDown={(e) => {
                  // Enter in a plain input must not submit the outer Generator form
                  if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
                    e.preventDefault()
                  }
                }}
                style={{
                  border: '1px dashed #94A3B8', borderRadius: 10,
                  padding: 16, marginTop: 12, background: '#FAFBFF',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: '#001A4D', marginBottom: 12 }}>
                  {editingPlantIdx !== null ? '✏️ Edit Plant' : '➕ Add New Plant'}
                </div>
                <Form form={plantForm} layout="vertical" component={false}>
                  <Form.Item name="plantName" label="Plant Name" rules={[{ required: true, message: 'Required' }]}>
                    <Input placeholder="e.g. Aralvaimozhi Hybrid Plant" />
                  </Form.Item>

                  <div style={col2}>
                    <Form.Item name="typeOfPower" label="Type of Power">
                      <Select
                        mode="tags"
                        placeholder="Select or type"
                        options={powerTypeOptions}
                        onChange={handlePowerTypeChange}
                      />
                    </Form.Item>
                    <Form.Item name="modeOfSupply" label="Mode of Supply">
                      <Select
                        mode="tags"
                        placeholder="Select or type"
                        options={supplyModeOptions}
                        onChange={handleSupplyModeChange}
                      />
                    </Form.Item>
                  </div>

                  <div style={col2}>
                    <Form.Item name="htServiceNumber" label="Plant HT No." rules={[{ required: true, message: 'Required' }]}>
                      <Input placeholder="e.g. HT-TN-WS-001" style={MONO} />
                    </Form.Item>
                    <Form.Item name="installedCapacityKw" label="Installed Capacity (kW)" rules={[{ required: true, message: 'Required' }]}>
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 5000" />
                    </Form.Item>
                  </div>

                  <Form.Item name="plantAddress" label="Plant Location">
                    <Input.TextArea rows={2} placeholder="Survey No, Village, District, State, PIN" />
                  </Form.Item>

                  <div style={col2}>
                    <Form.Item name="substationName" label="Plant Substation" rules={[{ required: true, message: 'Required' }]}>
                      <Input placeholder="e.g. Aralvaimozhi SS" />
                    </Form.Item>
                    <Form.Item name="substationVoltage" label="Substation Voltage (kV)">
                      <Select showSearch placeholder="Select voltage" options={voltageOptions} allowClear />
                    </Form.Item>
                  </div>

                  <div style={col2}>
                    <Form.Item name="cod" label="Commissioning Date (COD)">
                      <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="DD / MM / YYYY" />
                    </Form.Item>
                    <Form.Item name="synchronizationDate" label="Synchronization Date">
                      <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="DD / MM / YYYY" />
                    </Form.Item>
                  </div>

                  <div style={col2}>
                    <Form.Item
                      name="capacity"
                      label="Proposed Annual Generation (kWh/year)"
                      rules={[{ required: true, message: 'Required' }]}
                      extra={`≈ ${formatUnitsKWh(annualToMonthlyUnits(plantCapacityWatch))}/month`}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 5000000" />
                    </Form.Item>
                    <Form.Item name="injectingVoltage" label="Injecting Voltage (kV)">
                      <Select showSearch placeholder="Select voltage" options={voltageOptions} allowClear />
                    </Form.Item>
                  </div>

                  <div style={col2}>
                    <Form.Item name="edc" label="EDC (Electricity Distribution Circle)">
                      <Select showSearch optionFilterProp="label" placeholder="Select EDC" options={edcOptions} allowClear />
                    </Form.Item>
                    <Form.Item name="meterNumber" label="Meter Number">
                      <Input placeholder="e.g. MTR-1234567" style={MONO} />
                    </Form.Item>
                  </div>

                  <div style={col3}>
                    <Form.Item name="meterMake" label="Meter Make">
                      <Input placeholder="e.g. L&T / ABB / Secure" />
                    </Form.Item>
                    <Form.Item name="ctRatio" label="CT Ratio">
                      <Input placeholder="e.g. 200/5" style={MONO} />
                    </Form.Item>
                    <Form.Item name="ptRatio" label="PT Ratio">
                      <Input placeholder="e.g. 11000/110" style={MONO} />
                    </Form.Item>
                  </div>

                  <div style={col2}>
                    <Form.Item name="plantLatitude" label="Plant Latitude">
                      <Input placeholder="e.g. 8.3112" style={MONO} />
                    </Form.Item>
                    <Form.Item name="plantLongitude" label="Plant Longitude">
                      <Input placeholder="e.g. 77.4009" style={MONO} />
                    </Form.Item>
                  </div>

                  <Form.Item name="status" label="Status" initialValue="active">
                    <Select options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
                  </Form.Item>
                </Form>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                  <Button onClick={() => { setPlantModalOpen(false); plantForm.resetFields(); setEditingPlantIdx(null) }}>
                    Cancel
                  </Button>
                  <Button type="primary" onClick={handleSavePlant}>
                    {editingPlantIdx !== null ? 'Update Plant' : 'Add Plant'}
                  </Button>
                </div>
              </div>
            )}
          </Card>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {mode === 'edit' ? 'Update Generator' : 'Add Generator'}
            </Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={6} />

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ThunderboltOutlined style={{ color: '#F59E0B' }} /> Generator Master
          </h2>
          <p className="page-subtitle">Generator companies and their plants — referenced by Mapping, Meter Readings, and Payouts</p>
        </div>
        {canAdd && <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>Add Generator</Button>}
      </div>

      {/* ── Stat cards ── */}
      <KpiCardRow
        colProps={{ xs: 24, sm: 12, lg: 6 }}
        cards={[
          { label: 'Total Generators', value: generators.length, icon: <ThunderboltOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
          { label: 'Total Plants', value: generators.reduce((s, g) => s + (g.plants || []).length, 0), icon: <ApartmentOutlined style={{ fontSize: 24, color: '#F59E0B' }} />, iconBg: 'rgba(245,158,11,0.08)', borderColor: '#F59E0B' },
          { label: 'Total Proposed Annual Generation', value: formatIndianCompact(totalCap, { suffix: 'kWh/yr' }), icon: <BarChartOutlined style={{ fontSize: 24, color: '#2563EB' }} />, iconBg: 'rgba(37,99,235,0.08)', borderColor: '#2563EB' },
          { label: 'Total Proposed Monthly Generation', value: formatIndianCompact(annualToMonthlyUnits(totalCap), { suffix: 'kWh/mo' }), icon: <BarChartOutlined style={{ fontSize: 24, color: '#0EA5E9' }} />, iconBg: 'rgba(14,165,233,0.08)', borderColor: '#0EA5E9' },
        ]}
      />

      {/* ── Filter bar ── */}
      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by name, code or GST..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredGenerators}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} generators` }}
          scroll={{ x: true }}
          onRow={(record) => ({ onClick: () => goView(record.id), style: { cursor: 'pointer' } })}
        />
      </Card>


    </div>
  )
}

export default GeneratorMaster
